import * as React from 'react'
import { FC } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { graphql, Link, useStaticQuery } from 'gatsby'
import * as constants from '../../styles/constants.scss'
import { Colors, IPath, ROUTE_PATH } from '../../utils/constants'
import { cls } from '../../utils/utils'
import Image from '../Image/Image'
import Button from '../Button/Button'
import { ImageType } from '../Image/Image.types'
import SvgIcon from '../SVGIcon/SvgIcon'
import * as styles from './Header.module.scss'

export interface IFlag {
  link: string
  icon: ImageType
}

interface IInnerData {
  logo: ImageType
  flags: IFlag[]
}

interface IHeaderData {
  markdownRemark: {
    frontmatter: IInnerData
  }
}

interface IRenderLinksData {
  isMenuOpen: boolean
  toggleMenu: () => void
}

export const HeaderTemplate: FC<IInnerData> = (props) => {
  const [isMenuOpen, setMenuOpen] = useState(false)

  const toggleMenu = useCallback(() => {
    setMenuOpen((currentVal) => !currentVal)
  }, [])

  if (typeof window !== `undefined`) {
    const mediaScreenQuery = useMemo(() => window.matchMedia(`(min-width: ${constants.bpLarge})`), [])
    const [matches, setMatches] = useState(mediaScreenQuery.matches)

    useEffect(() => {
      mediaScreenQuery.onchange = (event: { matches: boolean }) => {
        setMatches(event.matches)
      }

      return () => {
        mediaScreenQuery.onchange = null
      }
    })

    if (matches && isMenuOpen) {
      toggleMenu()
    }
  }

  return (
    <header className={cls(styles.header, isMenuOpen ? styles.openMenu : '')} id="header">
      <div className={cls('main-container', styles.mainContainer)}>
        <div className={styles.logoContainer}>
          <Link to="/" className={styles.link} replace={typeof location !== `undefined` && location.pathname === ROUTE_PATH.services.path}>
            <Image alt="DSR logo" fixed image={props.logo} />
          </Link>
        </div>
        <button onClick={toggleMenu} aria-label="Menu Toggle" className={styles.toggle}>
          {!isMenuOpen && <SvgIcon href="/images/menu-button-open.svg#icon" className={styles.icon} />}
          {isMenuOpen && <SvgIcon href="/images/menu-button-close.svg#icon" className={styles.icon} />}
        </button>
        <nav role="none" className={styles.navigation}>
          <div className={styles.links}>{renderLinks({ isMenuOpen, toggleMenu })}</div>
          <div className={styles.connect}>
            <Link
              to={ROUTE_PATH.connectWithUs.path}
              className={styles.link}
              activeClassName={styles.activated}
              partiallyActive
              onClick={() => {
                if (location.pathname === ROUTE_PATH.connectWithUs.path && isMenuOpen) {
                  toggleMenu()
                }
              }}
              replace={typeof location !== `undefined` && location.pathname === ROUTE_PATH.connectWithUs.path}
            >
              <Button btnStyle={Colors.PRIMARY} className={styles.btn}>
                Connect with Us
              </Button>
            </Link>
          </div>

          <div className={styles.flags}>
            {props.flags.map((flag) => (
              <a href={flag.link} key={flag.link} className={styles.flag} target="_blank">
                <Image alt="Flag icon" fixed image={flag.icon} />
              </a>
            ))}
          </div>
        </nav>
      </div>
      {isMenuOpen && <div className={styles.overlay} onClick={() => toggleMenu()} />}
    </header>
  )
}

const Header = () => {
  const headerData: IHeaderData = useStaticQuery(query)
  const data = headerData.markdownRemark.frontmatter

  return <HeaderTemplate {...data} />
}

const renderLinks: FC<IRenderLinksData> = (props) => {
  const [isTagsPage, setIsTagsPage] = useState(false)

  useEffect(() => {
    setIsTagsPage(typeof location !== 'undefined' && location.pathname.startsWith('/tags'))
  }, [])
  return (
    <>
      {Object.keys(ROUTE_PATH).map((key) => {
        if (key === 'connectWithUs') return null
        const route = ROUTE_PATH[key as IPath]
        return (
          <div className={styles.linkContainer} key={key}>
            <Link
              to={route.path}
              className={cls(
                'text--small',
                styles.link,
                isTagsPage && route.path === ROUTE_PATH['press' as IPath].path && styles.activated
              )}
              activeClassName={styles.activated}
              partiallyActive
              onClick={() => {
                if (location.pathname === route.path && props.isMenuOpen) {
                  props.toggleMenu()
                }
              }}
              replace={typeof location !== `undefined` && location.pathname === route.path}
            >
              {route.name}
            </Link>
          </div>
        )
      })}
    </>
  )
}

export const query = graphql`
  query HeaderData {
    markdownRemark(frontmatter: { componentName: { eq: "Header" } }) {
      frontmatter {
        logo {
          childImageSharp {
            fixed(height: 40) {
              ...GatsbyImageSharpFixed
            }
          }
          publicURL
        }
        flags {
          link
          icon {
            childImageSharp {
              fixed(width: 30) {
                ...GatsbyImageSharpFixed
              }
            }
            publicURL
          }
        }
      }
    }
  }
`

export default Header
